import type { FormFieldDefinition, FormJtDocumentType, FormPathType } from '@engage/types';
import { DEFAULT_FORM_JT_DOCUMENT_TYPE, FORM_JT_DOCUMENT_TYPES } from '@engage/types';
import { useCandidateFieldKeys, type CandidateFieldKey } from '../../../api/forms.api';
import { Btn, Icon, ICONS, INPUT_STYLE, Pill, Row, Toggle } from '../ui/atoms';
import { FORM_UI_COLORS as C, FORM_UI_FONT } from '../ui/tokens';

const TYPE_LABELS: Record<FormFieldDefinition['type'], string> = {
  SHORT_TEXT: 'Short text',
  LONG_TEXT: 'Long text',
  EMAIL: 'Email',
  PHONE: 'Phone',
  NUMBER: 'Number',
  SINGLE_SELECT: 'Single select',
  MULTI_SELECT: 'Multi select',
  DATE: 'Date',
  FILE_UPLOAD: 'File upload',
  ADDRESS: 'Address',
  CONSENT_CHECKBOX: 'Consent checkbox',
};

export function FieldSettings(props: {
  field: FormFieldDefinition;
  pathType: FormPathType;
  earlierFields: FormFieldDefinition[];
  onChange: (patch: Partial<FormFieldDefinition>) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const { field, pathType } = props;
  const candidateKeys = useCandidateFieldKeys();
  const matchedKey = candidateKeys.data?.find((k) => k.key === field.key);
  // A file goes to the person's Documents, never a profile field (JT1-1923).
  const isUpdateable =
    pathType === 'CANDIDATE' && field.type !== 'FILE_UPLOAD' && field.updateable_on_candidate_path !== false;
  const boundsApply = isUpdateable && (matchedKey?.bounds_apply ?? false);

  return (
    <div style={{ fontFamily: FORM_UI_FONT }}>
      {/* header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Icon d={ICONS.grip} size={16} color={C.faint} />
          <span style={{ fontSize: 13, fontWeight: 700, color: C.indigo }}>
            {TYPE_LABELS[field.type]} field
          </span>
        </div>
        <button
          type="button"
          onClick={props.onClose}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: C.faint,
            padding: 4,
          }}
        >
          <Icon d={ICONS.x} size={16} color={C.faint} />
        </button>
      </div>

      <Row label="LABEL">
        <input
          value={field.label}
          onChange={(e) => props.onChange({ label: e.target.value })}
          style={INPUT_STYLE}
        />
      </Row>

      {isUpdateable ? (
        <Row
          label="MAPS TO JT FIELD (KEY)"
          hint="Candidate-path updateable fields map to a known JobTetris writable column. Picked from a list to avoid typos that would silently drop the update at sync."
        >
          <div style={{ position: 'relative' }}>
            <select
              value={field.key}
              onChange={(e) => {
                const picked = candidateKeys.data?.find((k) => k.key === e.target.value);
                props.onChange(picked ? linkPatch(field, picked) : { key: e.target.value });
              }}
              style={{ ...INPUT_STYLE, appearance: 'none', paddingRight: 30 }}
            >
              {candidateKeys.data?.map((k) => (
                <option key={k.key} value={k.key}>
                  {k.label} · {k.key.startsWith('custom_attribute:') ? 'custom field' : k.key}
                </option>
              ))}
              {!candidateKeys.data?.some((k) => k.key === field.key) && (
                <option value={field.key}>{field.key}</option>
              )}
            </select>
            <span style={{ position: 'absolute', right: 10, top: 11, pointerEvents: 'none' }}>
              <Icon d={ICONS.chev} size={13} color={C.faint} sw={2} />
            </span>
          </div>
        </Row>
      ) : (
        <Row label="KEY">
          <input
            value={field.key}
            onChange={(e) => props.onChange({ key: slugifyKey(e.target.value) })}
            style={INPUT_STYLE}
          />
        </Row>
      )}

      <Row label="HELP TEXT">
        <textarea
          rows={2}
          value={field.help_text ?? ''}
          placeholder="Optional helper shown under the field"
          onChange={(e) => props.onChange({ help_text: e.target.value || undefined })}
          style={{ ...INPUT_STYLE, fontFamily: FORM_UI_FONT, resize: 'vertical' }}
        />
      </Row>

      <Toggle
        on={field.required}
        label="Required"
        sub="Form can't be submitted without this field"
        onChange={(v) => props.onChange({ required: v })}
      />

      {/* Type-specific: select options */}
      {(field.type === 'SINGLE_SELECT' || field.type === 'MULTI_SELECT') && (
        <Row label="OPTIONS">
          <OptionsEditor
            options={field.options}
            onChange={(options) => props.onChange({ options } as Partial<FormFieldDefinition>)}
          />
        </Row>
      )}

      {/* Type-specific: where an uploaded file is saved in JobTetris (JT1-1923) */}
      {field.type === 'FILE_UPLOAD' && (
        <Row
          label="SAVE IN JOBTETRIS AS"
          hint="The file goes into the person's Documents in JobTetris as this document type."
        >
          <select
            value={field.jt_document_type ?? DEFAULT_FORM_JT_DOCUMENT_TYPE}
            onChange={(e) =>
              props.onChange({ jt_document_type: e.target.value as FormJtDocumentType } as Partial<FormFieldDefinition>)
            }
            style={INPUT_STYLE}
            aria-label="Save in JobTetris as"
          >
            {FORM_JT_DOCUMENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </Row>
      )}

      {/* Candidate integrity block — the heart of the candidate-path UX */}
      {pathType === 'CANDIDATE' && field.type !== 'CONSENT_CHECKBOX' && field.type !== 'FILE_UPLOAD' && (
        <CandidateIntegrityBlock
          field={field}
          boundsApply={boundsApply}
          onChange={props.onChange}
        />
      )}

      {/* Conditional logic */}
      <ConditionalLogicEditor
        field={field}
        earlierFields={props.earlierFields}
        onChange={(conditional_logic) => props.onChange({ conditional_logic })}
      />

      {/* Delete */}
      <div style={{ marginTop: 18 }}>
        <Btn kind="danger" onClick={props.onRemove}>
          <Icon d={ICONS.x} size={14} color={C.danger} />
          Delete this field
        </Btn>
      </div>
    </div>
  );
}

// ─── Candidate Integrity block ───────────────────────────────────────────────

function CandidateIntegrityBlock(props: {
  field: FormFieldDefinition;
  boundsApply: boolean;
  onChange: (patch: Partial<FormFieldDefinition>) => void;
}) {
  const { field, onChange } = props;
  return (
    <div
      style={{
        marginTop: 18,
        padding: 14,
        background: C.candidateBg,
        borderRadius: 11,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          marginBottom: 4,
        }}
      >
        <Icon d={ICONS.shield} size={15} color={C.indigo} />
        <span
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: C.indigo,
            letterSpacing: 0.4,
          }}
        >
          CANDIDATE INTEGRITY
        </span>
      </div>
      <div
        style={{
          fontSize: 10.8,
          color: C.indigoSoft,
          marginBottom: 6,
          lineHeight: 1.45,
        }}
      >
        Controls how a submitted value is written back to the JobTetris candidate record.
      </div>

      <Toggle
        on={field.updateable_on_candidate_path !== false}
        label="Updateable"
        sub="Allow this submission to change the candidate record"
        onChange={(v) =>
          onChange({ updateable_on_candidate_path: v ? true : false })
        }
      />

      {field.updateable_on_candidate_path !== false && (
        <>
          <Toggle
            on={field.validation_rules?.blank_out_protected !== false}
            label="Blank-out protected"
            sub="Candidate can change the value but can't wipe it to empty"
            onChange={(v) =>
              onChange({
                validation_rules: {
                  ...field.validation_rules,
                  blank_out_protected: v ? undefined : false,
                },
              })
            }
          />

          {props.boundsApply && (
            <>
              <Row
                label="REJECTION THRESHOLD"
                hint="Reject the whole submission if this value changes by more than the bound."
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    placeholder="-"
                    value={field.validation_rules?.percentage_delta ?? ''}
                    onChange={(e) =>
                      onChange({
                        validation_rules: {
                          ...field.validation_rules,
                          percentage_delta:
                            e.target.value === '' ? undefined : Number(e.target.value),
                        },
                      })
                    }
                    style={{ ...INPUT_STYLE, width: 70 }}
                  />
                  <span style={{ fontSize: 12.5, color: C.sub, fontWeight: 600 }}>% change</span>
                </div>
              </Row>

              <Row
                label="SIGNIFICANT-CHANGE FLAG"
                hint="Auto-apply, but notify the recruiter when the change exceeds this independent bound."
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    placeholder="-"
                    value={field.validation_rules?.significant_change_threshold ?? ''}
                    onChange={(e) =>
                      onChange({
                        validation_rules: {
                          ...field.validation_rules,
                          significant_change_threshold:
                            e.target.value === '' ? undefined : Number(e.target.value),
                        },
                      })
                    }
                    style={{ ...INPUT_STYLE, width: 70 }}
                  />
                  <span style={{ fontSize: 12.5, color: C.sub, fontWeight: 600 }}>% change</span>
                </div>
              </Row>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ─── Conditional logic ───────────────────────────────────────────────────────

function ConditionalLogicEditor(props: {
  field: FormFieldDefinition;
  earlierFields: FormFieldDefinition[];
  onChange: (rule: FormFieldDefinition['conditional_logic']) => void;
}) {
  const rule = props.field.conditional_logic;
  const referenced = props.earlierFields.find((f) => f.key === rule?.field_key);

  return (
    <Row
      label="CONDITIONAL LOGIC"
      hint="Forward-only: can only reference fields above this one."
    >
      <div style={{ marginBottom: 8 }}>
        <Toggle
          on={!!rule}
          label="Show only when…"
          onChange={(v) =>
            props.onChange(
              v
                ? {
                    field_key: props.earlierFields[0]?.key ?? '',
                    operator: 'equals',
                    value: '',
                  }
                : undefined
            )
          }
        />
      </div>
      {rule && (
        <div
          style={{
            background: C.bg,
            border: `1px solid ${C.line}`,
            borderRadius: 10,
            padding: '11px 12px',
            fontSize: 12,
            color: C.indigoSoft,
            lineHeight: 1.9,
          }}
        >
          Show this field only when{' '}
          <Pill>{referenced?.label ?? rule.field_key}</Pill>
          <Pill>{rule.operator.replace('_', ' ')}</Pill>
          <Pill>{String(rule.value) || '…'}</Pill>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <select
              value={rule.field_key}
              onChange={(e) => props.onChange({ ...rule, field_key: e.target.value })}
              style={{ ...INPUT_STYLE, flex: 1 }}
            >
              {props.earlierFields.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label || f.key}
                </option>
              ))}
            </select>
            <select
              value={rule.operator}
              onChange={(e) =>
                props.onChange({ ...rule, operator: e.target.value as typeof rule.operator })
              }
              style={{ ...INPUT_STYLE, flex: 1 }}
            >
              <option value="equals">equals</option>
              <option value="not_equals">does not equal</option>
              <option value="contains">contains</option>
            </select>
          </div>
          <input
            value={String(rule.value)}
            onChange={(e) => props.onChange({ ...rule, value: e.target.value })}
            placeholder="value"
            style={{ ...INPUT_STYLE, marginTop: 6 }}
          />
        </div>
      )}
    </Row>
  );
}

// ─── Options editor ──────────────────────────────────────────────────────────

function OptionsEditor(props: {
  options: { label: string; value: string }[];
  onChange: (next: { label: string; value: string }[]) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {props.options.map((opt, idx) => (
        <div key={idx} style={{ display: 'flex', gap: 6 }}>
          <input
            type="text"
            placeholder="Label"
            value={opt.label}
            onChange={(e) => {
              const next = [...props.options];
              next[idx] = { ...opt, label: e.target.value };
              props.onChange(next);
            }}
            style={{ ...INPUT_STYLE, flex: 1 }}
          />
          <input
            type="text"
            placeholder="Value"
            value={opt.value}
            onChange={(e) => {
              const next = [...props.options];
              next[idx] = { ...opt, value: e.target.value };
              props.onChange(next);
            }}
            style={{ ...INPUT_STYLE, flex: 1 }}
          />
          <button
            type="button"
            onClick={() => props.onChange(props.options.filter((_, i) => i !== idx))}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 4,
            }}
          >
            <Icon d={ICONS.x} size={14} color={C.faint} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => props.onChange([...props.options, { label: '', value: '' }])}
        style={{
          ...INPUT_STYLE,
          color: C.tealDark,
          fontWeight: 600,
          cursor: 'pointer',
          border: `1px dashed ${C.teal}`,
          textAlign: 'center',
          background: '#fff',
        }}
      >
        + Add option
      </button>
    </div>
  );
}

/**
 * Linking a form field to a profile field (JT1-1924): the key, plus the question that field
 * needs, such as a multi-select of JobTetris's work rights. The label and help text are only
 * filled in while still the defaults, so a builder's own wording stays.
 */
export function linkPatch(field: FormFieldDefinition, picked: CandidateFieldKey): Partial<FormFieldDefinition> {
  const patch: Record<string, unknown> = { key: picked.key };
  if (picked.field_type && picked.field_type !== field.type) patch.type = picked.field_type;
  if (picked.options && (picked.field_type === 'SINGLE_SELECT' || picked.field_type === 'MULTI_SELECT')) {
    patch.options = picked.options;
  }
  if (picked.hint && !field.help_text) patch.help_text = picked.hint;
  return patch as Partial<FormFieldDefinition>;
}

function slugifyKey(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9_:]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}
